// Kosten delen: saldi, voorstel om te verrekenen en afsluiten (golf 8, 0049_samen_wonen;
// docs/onderzoek/golf8-samenwonen-en-ouderen.md §2.3.3 en §2.4.4). Puur en in hele centen.
//
// Saldo per lid = overgenomen saldo (laatste afsluiting) + Σ betaald − Σ aandeel
//                 + Σ verrekend (betaald aan een ander) − Σ verrekend (ontvangen).
// Alleen niet-verwijderde regels na de laatste afsluiting, en alleen BEVESTIGDE verrekeningen.
// Positief = krijgt nog geld; negatief = moet nog verrekenen. De som is altijd 0.
import { addDays } from '../../recurrence/localDate.js';
/** Saldo per lid (alleen leden die ergens in voorkomen). */
export function balances(carry, expenses, shares, settlements) {
    const out = new Map();
    const add = (id, cents) => out.set(id, (out.get(id) ?? 0) + cents);
    for (const c of carry)
        add(c.member_id, c.cents);
    const live = new Set();
    for (const e of expenses) {
        if (e.deleted)
            continue;
        live.add(e.id);
        add(e.paid_by_member_id, e.amount_cents);
    }
    for (const s of shares)
        if (live.has(s.expense_id))
            add(s.member_id, -s.share_cents);
    for (const s of settlements) {
        if (s.deleted || !s.confirmed)
            continue;
        add(s.from_member_id, s.amount_cents);
        add(s.to_member_id, -s.amount_cents);
    }
    return out;
}
/**
 * Voorstel om te verrekenen: steeds de grootste schuldenaar aan de grootste ontvanger (greedy,
 * zoals de bekende apps; het echte minimum is in het algemeen NP-moeilijk). Hooguit n−1
 * overmakingen, daarna staat iedereen op 0. Deterministisch: bij gelijke bedragen op id.
 */
export function settleUp(balance) {
    const byId = (a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
    const creditors = [...balance]
        .filter(([, c]) => c > 0)
        .map(([id, c]) => ({ id, left: c }))
        .sort((a, b) => b.left - a.left || byId(a, b));
    const debtors = [...balance]
        .filter(([, c]) => c < 0)
        .map(([id, c]) => ({ id, left: -c }))
        .sort((a, b) => b.left - a.left || byId(a, b));
    const out = [];
    while (creditors.length > 0 && debtors.length > 0) {
        const c = creditors[0];
        const d = debtors[0];
        const cents = Math.min(c.left, d.left);
        out.push({ from: d.id, to: c.id, cents });
        c.left -= cents;
        d.left -= cents;
        if (c.left === 0)
            creditors.shift();
        if (d.left === 0)
            debtors.shift();
        creditors.sort((a, b) => b.left - a.left || byId(a, b));
        debtors.sort((a, b) => b.left - a.left || byId(a, b));
    }
    return out;
}
/**
 * Tot welke dag kan er afgesloten worden (voor de kaart "Periode afsluiten")? De laatste dag vóór de
 * eerste open vraag of wachtende verrekening, en nooit na vandaag. null als dat niet na de vorige
 * afsluiting ligt.
 */
export function closableThrough(today, lastThrough, expenses, settlements) {
    let limit = today;
    for (const e of expenses) {
        if (!e.deleted && e.question_open && e.spent_on <= limit)
            limit = addDays(e.spent_on, -1);
    }
    for (const s of settlements) {
        if (!s.deleted && !s.confirmed && s.settled_on <= limit)
            limit = addDays(s.settled_on, -1);
    }
    if (lastThrough !== null && limit <= lastThrough)
        return null;
    return limit;
}
// Review golf 8 r2: wie geld krijgt, stuurt het betaalverzoek (Tikkie); wie betaalt, maakt het
// over. Twee teksten, zonder schuldwoorden ("borç", "owe", "schuld").
const COPY = {
    receiver: {
        nl: 'Hoi {to}, volgens ons huisoverzicht krijg ik nog {amount} van je (t/m {through}). Ik stuur je een betaalverzoek.',
        en: 'Hi {to}, according to our house overview I still get {amount} from you (up to {through}). I will send you a payment request.',
        tr: 'Merhaba {to}, ev özetimize göre senden {amount} almam gerekiyor ({through} tarihine kadar). Sana bir ödeme isteği gönderiyorum.',
        ar: 'مرحبًا {to}، حسب ملخص بيتنا سأستلم منك {amount} (حتى {through}). سأرسل لك طلب دفع.',
        de: 'Hallo {to}, laut unserer Hausübersicht bekomme ich noch {amount} von dir (bis {through}). Ich schicke dir eine Zahlungsanfrage.',
        fr: 'Salut {to}, d’après notre récapitulatif de la maison, je reçois encore {amount} de ta part (jusqu’au {through}). Je t’envoie une demande de paiement.',
    },
    payer: {
        nl: 'Hoi {to}, volgens ons huisoverzicht krijg jij nog {amount} van mij (t/m {through}). Ik maak het vandaag over.',
        en: 'Hi {to}, according to our house overview you still get {amount} from me (up to {through}). I will transfer it today.',
        tr: 'Merhaba {to}, ev özetimize göre benden {amount} alman gerekiyor ({through} tarihine kadar). Bugün gönderiyorum.',
        ar: 'مرحبًا {to}، حسب ملخص بيتنا ستستلم مني {amount} (حتى {through}). سأحوّله اليوم.',
        de: 'Hallo {to}, laut unserer Hausübersicht bekommst du noch {amount} von mir (bis {through}). Ich überweise es heute.',
        fr: 'Salut {to}, d’après notre récapitulatif de la maison, tu reçois encore {amount} de ma part (jusqu’au {through}). Je fais le virement aujourd’hui.',
    },
};
const INTL = {
    nl: 'nl-NL',
    en: 'en-GB',
    tr: 'tr-TR',
    ar: 'ar',
    de: 'de-DE',
    fr: 'fr-FR',
};
/** Een bedrag in euro in de taal van de gebruiker (zoals euro() in de webapp). */
export function formatEuro(cents, locale) {
    const lang = (locale ?? 'nl').slice(0, 2).toLowerCase();
    return new Intl.NumberFormat(INTL[lang] ?? 'nl-NL', {
        style: 'currency',
        currency: 'EUR',
    }).format(cents / 100);
}
/**
 * De tekst voor een betaalverzoek (KD-3), in de taal van de gebruiker. Zonder IBAN of link: er gaat
 * geen geld via AMUMIN. `as: 'receiver'` (standaard): wie geld krijgt, schrijft aan wie betaalt
 * ("krijg ik nog … van je; ik stuur je een betaalverzoek"); `'payer'`: wie betaalt, schrijft aan
 * wie krijgt ("… ik maak het vandaag over"). `to` is steeds de ander.
 */
export function copyText(locale, input) {
    const lang = (locale ?? 'nl').slice(0, 2).toLowerCase();
    const set = COPY[input.as ?? 'receiver'];
    const tpl = set[lang] ?? set.nl ?? '';
    const [y, m, d] = input.through.split('-').map(Number);
    const through = new Intl.DateTimeFormat(INTL[lang] ?? 'nl-NL', {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
    }).format(new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)));
    return tpl
        .split('{to}')
        .join(input.to)
        .split('{amount}')
        .join(formatEuro(input.cents, locale))
        .split('{through}')
        .join(through);
}
//# sourceMappingURL=balance.js.map